import React, { useEffect, useMemo } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { assessmentPeriodApi } from '../api/assessmentPeriodApi';
import { ASSESSMENT_PERIOD_CLOSED, ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_OPEN } from '../constants';

type AssessmentPeriodListProps = {
  currentOrganizationId?: number | null;
  selectedAssessmentPeriodId: string | number | undefined | null;
  assessmentPeriods?: any[];
  // onChange now optionally provides the selected period object as a second argument to avoid
  // consumers needing to re-fetch or rely on potentially stale store data.
  onChange: (id: string | number | undefined, period?: any) => void;
  status?: (typeof ASSESSMENT_PERIOD_OPEN | typeof ASSESSMENT_PERIOD_CLOSED | typeof ASSESSMENT_PERIOD_IN_PROGRESS)[];
};

const AssessmentPeriodList: React.FC<AssessmentPeriodListProps> = ({
  currentOrganizationId,
  selectedAssessmentPeriodId,
  assessmentPeriods: providedAssessmentPeriods,
  onChange,
  status
}) => {
  const useProvidedAssessmentPeriods = Array.isArray(providedAssessmentPeriods);
  const {
    data: fetchedAssessmentPeriods = [],
    isLoading,
    refetch,
  } = assessmentPeriodApi.useApiPortalV1AssessmentsListQuery(undefined, {
    skip: useProvidedAssessmentPeriods,
  });

  const assessmentPeriods = useProvidedAssessmentPeriods ? providedAssessmentPeriods : fetchedAssessmentPeriods;

  const options = useMemo(
    () =>
      [...assessmentPeriods]
        .filter(p => !status?.length || (p.status !== undefined && status.includes(p.status)))
        .sort(
          (a, b) =>
            new Date(b.start_date_time).getTime() -
            new Date(a.start_date_time).getTime()
        )
        .map(p => ({ key: p.id, value: p.id, text: p.name })),
    [assessmentPeriods, status]
  );

  React.useEffect(() => {
    if (useProvidedAssessmentPeriods) return;
    if (currentOrganizationId == null) return;
    refetch();
  }, [currentOrganizationId, refetch, useProvidedAssessmentPeriods]);

  // Auto-select most recent only if no period has yet been chosen.
  // Do not override an existing selection simply because the refreshed options list
  // temporarily does not contain the current selection.
  useEffect(() => {
    if (isLoading || options.length === 0) return;

    if (selectedAssessmentPeriodId == null || selectedAssessmentPeriodId === 0) {
      const defaultId = options[0].value;
      const defaultPeriod = assessmentPeriods.find(p => p.id === defaultId);
      onChange(defaultId, defaultPeriod);
    }
  }, [isLoading, options, selectedAssessmentPeriodId, onChange, assessmentPeriods]);

  return (
    <Dropdown
      placeholder="-"
      options={options}
      value={selectedAssessmentPeriodId ?? undefined}
      onChange={(_, data) => {
        const selectedId = data.value as any;
        const selectedPeriod = assessmentPeriods.find(p => p.id === selectedId);
        onChange(selectedId, selectedPeriod);
      }}
      style={{
        width: '100%',
        fontSize: '14px',
        fontWeight: 550,
        border: 'none',
        height: '8px',
        lineHeight: '8px',
        paddingLeft: '0px',
        paddingTop: '0px',
        boxShadow: 'none',
        textAlign: 'left',
        minWidth: '80px',
      }}
    />
  );
};

export default AssessmentPeriodList;